import React, { useContext, useEffect, useState } from "react"
import propTypes from "prop-types"
import i18n from "i18n"
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Stack,
  Slide,
} from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded"
import { useParams } from "react-router-dom"
import { isNil } from "lodash"
import ProjectContext from "context/projects/projectContext"
import AuthContext from "context/auth/authContext"
import DownloadBlockModal from "./DownloadBlockModal"
import Budget2023Context from "context/budget2023/Budget2023Context"
import ModalCallError from "common/modalCallError"
import ModalPDFPreview from "./ModalPDFPreview"
import * as amplitude from "@amplitude/analytics-browser"
import DownloadBudgetEnum from "common/DownloadBudgetEnum"
import CertHistoryContext from "context/certHistory/CertHistoryContext"

import ModalWaitProcess from "common/ModalWaitProcess"
import useDownloadBudgetConfig, {
  DOWNLOAD_TYPES,
  INPUTS,
} from "hooks/budgeting/useDownloadBudgetConfig"

import { useExcelOptions } from "hooks/downloadBudget/useExcelOptions"
import ExcelOptions from "./ExcelOptions"
import { usePdfOptions } from "hooks/downloadBudget/usePdfOptions"
import PdfOptions from "./PdfOptions"
import FilterToggleButtonGroup from "./FilterToggleButtonGroup"
import BC3Options from "./BC3Options"
import { useBC3Options } from "hooks/downloadBudget/useBC3Options"
import useEntitlementGuard from "hooks/useEntitlementGuard"
import { EXPORT_BC3, EXPORT_PDF } from "constants/entitlements"

const MODULE_TITLE_COLOR = "#414141"

const PanelSlide = React.forwardRef(function PanelSlide(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />
})
PanelSlide.displayName = "PanelSlide"

const LEGACY = "legacy"
const PRICE = "price"

const DownloadBudget = ({
  open,
  setOpen,
  name,
  soyFinal,
  fromTab = DownloadBudgetEnum.DEFAULT,
}) => {
  const { projectId } = useParams()
  const { projectInfo, getProject } = useContext(ProjectContext)
  const { exportSideBar } = useContext(CertHistoryContext)
  const {
    downloadBudget,
    urlDownload,
    pdfDownload,
    downloadCertification,
    downloadCertificationAtOriginXls,
    downloadCertificationAtOriginPdf,
    bc3Download,
  } = useContext(Budget2023Context)

  const [downloadBudgetConfig, setDownloadBudgetConfig] =
    useDownloadBudgetConfig()

  const [checkEntitlement, entitlementDialog] = useEntitlementGuard()

  const excelData = useExcelOptions({
    downloadBudgetConfig,
  })
  const pdfData = usePdfOptions({
    downloadBudgetConfig,
  })
  const bc3Data = useBC3Options({
    downloadBudgetConfig,
  })

  const DESCRIPTION = true

  const typeBudget = soyFinal ? "PF" : "PC"
  const { user, getUser } = useContext(AuthContext)
  const [tipo, setTipo] = useState(DOWNLOAD_TYPES.EXCEL)

  const [openPreview, setOpenPreview] = useState(false)

  const [openModalWaitProcess, setOpenModalWaitProcess] = useState(false)

  useEffect(() => {
    if (!projectInfo) {
      getProject(projectId)
    }
    if (!user) {
      getUser()
    }
  }, [])

  const [openError, setOpenError] = useState(false)
  const [errorToSend, setErrorToSend] = useState("")
  const [openBlockModal, setOpenBlockModal] = useState(false)
  const [urlState, setUrlState] = useState("")

  const [processState, setProcessState] = useState({})
  const [excelCertificationAtOrigin, setExcelCertificationAtOrigin] =
    useState(false)
  const [pdfCertificationAtOrigin, setPdfCertificationAtOrigin] =
    useState(false)
  const [pdfIncludeBudget, setPdfIncludeBudget] = useState(false)
  const [pdfCertificationFormat, setPdfCertificationFormat] =
    useState("detailed")
  const [pdfIncludeUncertifiedLineItems, setPdfIncludeUncertifiedLineItems] =
    useState(false)

  useEffect(() => {
    if (!open) {
      setExcelCertificationAtOrigin(false)
      setPdfCertificationAtOrigin(false)
      setPdfIncludeBudget(false)
      setPdfCertificationFormat("detailed")
      setPdfIncludeUncertifiedLineItems(false)
      return
    }
    if (
      fromTab === DownloadBudgetEnum.FROM_HISTORY_CERTIFICATION ||
      fromTab === DownloadBudgetEnum.FROM_CERTIFICATION
    ) {
      setExcelCertificationAtOrigin(
        Boolean(
          downloadBudgetConfig?.[DOWNLOAD_TYPES.EXCEL]?.[
            INPUTS.CERTIFICATION_AT_ORIGIN
          ],
        ),
      )
      setPdfCertificationAtOrigin(
        Boolean(
          downloadBudgetConfig?.[DOWNLOAD_TYPES.PDF]?.[
            INPUTS.CERTIFICATION_AT_ORIGIN
          ],
        ),
      )
      setPdfIncludeBudget(
        Boolean(
          downloadBudgetConfig?.[DOWNLOAD_TYPES.PDF]?.[INPUTS.INCLUDE_BUDGET],
        ),
      )
      setPdfCertificationFormat(
        downloadBudgetConfig?.[DOWNLOAD_TYPES.PDF]?.[
          INPUTS.CERTIFICATION_PDF_FORMAT
        ] ?? "detailed",
      )
      setPdfIncludeUncertifiedLineItems(
        Boolean(
          downloadBudgetConfig?.[DOWNLOAD_TYPES.PDF]?.[
            INPUTS.INCLUDE_UNCERTIFIED_LINE_ITEMS
          ],
        ),
      )
    }
  }, [open, fromTab, downloadBudgetConfig])

  const downloadExcelFromCertificationTab = async () => {
    if (excelCertificationAtOrigin) {
      const result = await downloadCertificationAtOriginXls(projectId, name)
      if (result?.error) {
        setOpenError(true)
        setErrorToSend(result)
      }
      return
    }

    const options = {
      decomposition: excelData.decomposition,
      measures: excelData.measure,
      comment: excelData.comments,
      decompositionFilter: excelData.decompositionFilter,
    }

    let result = await downloadCertification(
      projectId,
      name,
      options,
      exportSideBar.selectedCertificationId || "",
    )

    if (result?.error) {
      setOpenError(true)
      setErrorToSend(result)
    }
  }

  const downloadExcelFromHistoryCertificationTab = async () => {
    if (excelCertificationAtOrigin) {
      const result = await downloadCertificationAtOriginXls(
        projectId,
        name,
        exportSideBar.selectedCertificationId,
      )
      if (result?.error) {
        setOpenError(true)
        setErrorToSend(result)
      }
      return
    }

    const options = {
      cost_price: excelData.cost_price,
      cost_real: excelData.cost_real,
      client_price: excelData.client_price,
      decomposition: excelData.decomposition,
      decomposition_filter: excelData.decompositionFilter,
      measures: excelData.measures,
      comment: excelData.comment,
      certifications: true,
      certification_id: exportSideBar.selectedCertificationId,
      currency: user["custom:currency"],
      debug: false,
    }

    let result = await exportSideBar.downloadHistoryCertification(
      projectId,
      name,
      exportSideBar.selectedCertificationId,
      options,
    )

    if (result?.error) {
      setOpenError(true)
      setErrorToSend(result)
    }
  }

  const downloadExcelFromDefault = async () => {
    const options = {
      cost: excelData.costPrice,
      price: excelData.clientPrice,
      decomposition: excelData.decomposition,
      measures: excelData.measure,
      comment: excelData.comments,
      decompositionFilter: excelData.decompositionFilter,
      currency: user["custom:currency"],
    }

    let result = await downloadBudget(
      projectId,
      name,
      tipo,
      typeBudget,
      options,
    )
    if (result?.error) {
      setOpenError(true)
      setErrorToSend(result)
    }
  }

  const excelDownloadActions = {
    [DownloadBudgetEnum.DEFAULT]: { run: downloadExcelFromDefault },
    [DownloadBudgetEnum.FROM_CERTIFICATION]: {
      run: downloadExcelFromCertificationTab,
    },
    [DownloadBudgetEnum.FROM_HISTORY_CERTIFICATION]: {
      run: downloadExcelFromHistoryCertificationTab,
    },
  }

  const triggerExcelDownload = async () => {
    const actionSelected = excelDownloadActions[fromTab]
    await actionSelected?.run?.()
  }

  const getOptions = () => {
    const isCertification = fromTab === DownloadBudgetEnum.FROM_CERTIFICATION

    let options = {
      envelope: pdfData.companyAndCustomerInfo,
      borderless: !pdfData.bordes,
      description: DESCRIPTION, //! check if this parameter is necessary
      cover: pdfData.addCover,
      briefing: true
        ? pdfData.briefingPosition
          ? { position: pdfData.briefingPosition }
          : {}
        : pdfData.briefingPosition,
      price: isCertification ? "client" : pdfData.clientPrice,
      mode: PRICE,
      decomposition:
        tipo === DOWNLOAD_TYPES.EXCEL
          ? excelData.decomposition
          : tipo === DOWNLOAD_TYPES.PDF
            ? pdfData.decomposition
            : true,
      measures: pdfData.pdfMeasurements,
      preview: false,
      conditions: pdfData.generalConditions,
      signature: pdfData.addSignature,
      header_date: pdfData.addDate,
      currency: "EUR",
      language: "es",
    }

    if (isCertification) {
      options = {
        ...options,
        mode: "certification",
        certifications: true,
        certificationId: "",
      }
    }

    return options
  }

  const triggerPdfDownload = async () => {
    const isAtOriginTab =
      fromTab === DownloadBudgetEnum.FROM_HISTORY_CERTIFICATION ||
      fromTab === DownloadBudgetEnum.FROM_CERTIFICATION

    if (isAtOriginTab && pdfCertificationAtOrigin) {
      const certificationId =
        fromTab === DownloadBudgetEnum.FROM_HISTORY_CERTIFICATION
          ? exportSideBar.selectedCertificationId
          : undefined
      const result = await downloadCertificationAtOriginPdf(
        projectId,
        name,
        certificationId,
        {
          includeBudget: pdfIncludeBudget,
          format: pdfCertificationFormat,
          includeAllParts: pdfIncludeUncertifiedLineItems,
        },
      )
      if (result?.error) {
        setOpenError(true)
        setErrorToSend(result)
      }
      return
    }

    const options = getOptions()

    let optionObj = options
    if (fromTab === DownloadBudgetEnum.FROM_HISTORY_CERTIFICATION) {
      optionObj = {
        briefing: {},
        decomposition: false,
        mode: "certification",
        price: "client",
        certificationId: exportSideBar.selectedCertificationId,
      }
    }

    const result = await pdfDownload(user, projectId, optionObj)

    if (result?.error) {
      setOpenError(true)
      setErrorToSend(result)
    } else {
      setProcessState(result)
      setOpenModalWaitProcess(true)
    }
  }

  const triggerBC3Download = async () => {
    const options = {
      addCosts: bc3Data.addCosts,
      addMeasurements: bc3Data.addMeasurements,
      addDecompositions: bc3Data.addDecompositions,
      simpleMode: bc3Data.simpleMode,
      name: name,
    }
    const result = await bc3Download(projectId, options)
    if (result?.error) {
      setOpenError(true)
      setErrorToSend(result)
    }
  }

  const downloadActions = {
    [DOWNLOAD_TYPES.EXCEL]: {
      event: "Budget Excel Downloaded",
      run: triggerExcelDownload,
    },
    [DOWNLOAD_TYPES.PDF]: {
      event: "Budget PDF Downloaded",
      run: triggerPdfDownload,
    },
    [DOWNLOAD_TYPES.BC3]: {
      event: "Budget BC3 Downloaded",
      run: triggerBC3Download,
    },
  }

  const getPreviewOptions = () => {
    if (fromTab === DownloadBudgetEnum.FROM_HISTORY_CERTIFICATION) {
      return {
        briefing: {},
        decomposition: false,
        mode: "certification",
        price: "client",
        certificationId: exportSideBar.selectedCertificationId,
      }
    }
    return getOptions()
  }

  const handlePreview = async () => {
    setOpenPreview(true)
  }

  const handleDownload = async e => {
    e.preventDefault()

    const actionSelected = downloadActions[tipo]

    if (!actionSelected) return

    if (
      tipo === DOWNLOAD_TYPES.PDF &&
      !checkEntitlement(EXPORT_PDF, "Exportar a PDF")
    )
      return

    if (
      tipo === DOWNLOAD_TYPES.BC3 &&
      !checkEntitlement(EXPORT_BC3, "Exportar a BC3")
    )
      return

    amplitude.track({
      event_type: actionSelected.event,
      event_properties: { projectId, projectName: name },
    })

    await actionSelected.run()
  }

  useEffect(() => {
    if (urlDownload == "done") {
      setOpen(false)
    }
  }, [urlDownload])

  const handleChangeTipo = (_event, newTipo) => {
    if (newTipo !== null) {
      setTipo(newTipo)
    }
  }

  const downloadOptionsComponents = {
    [DOWNLOAD_TYPES.EXCEL]: {
      Component: ExcelOptions,
      data: excelData,
    },
    [DOWNLOAD_TYPES.PDF]: {
      Component: PdfOptions,
      data: pdfData,
    },
    [DOWNLOAD_TYPES.BC3]: {
      Component: BC3Options,
      data: bc3Data,
    },
  }

  const SelectedDownloadComponent = downloadOptionsComponents[tipo]?.Component
  const selectedDownloadData = downloadOptionsComponents[tipo]?.data

  const isCertTab =
    fromTab === DownloadBudgetEnum.FROM_CERTIFICATION ||
    fromTab === DownloadBudgetEnum.FROM_HISTORY_CERTIFICATION

  const handleClose = () => {
    setOpen(false)
    exportSideBar.setSelectedCertificationId(null)
  }

  const isDownloadPending = urlDownload === "pending" || exportSideBar.isLoading

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={PanelSlide}
        keepMounted={false}
        PaperProps={{
          elevation: 0,
          "data-testid": "download-budget-modal",
          sx: {
            position: "fixed",
            right: 0,
            m: 0,
            maxWidth: 350,
            width: "100%",
            height: "100%",
            maxHeight: "100%",
            borderRadius: "4px 0 0 4px",
            boxShadow: "none",
            borderLeft: "1px solid #eee",
            bgcolor: "#fff",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            justifyContent: "flex-end",
            alignItems: "stretch",
          },
        }}
      >
        <Box
          component="form"
          onSubmit={handleDownload}
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            height: "100%",
          }}
        >
          <DialogContent
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              pt: 2,
              px: 2,
              pb: 1,
            }}
          >
            <Stack spacing={3}>
              <IconButton
                type="button"
                size="small"
                aria-label="close"
                onClick={handleClose}
                data-testid="download-budget-modal-close"
                sx={{
                  width: 32,
                  height: 32,
                }}
              >
                <ArrowForwardIosRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>

              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: MODULE_TITLE_COLOR,
                  fontFamily: "Helvetica, sans-serif",
                }}
              >
                {i18n.t("downloadBudget.downloadBudget")} {name}
              </Typography>

              <Box display="flex" justifyContent="flex-start">
                <FilterToggleButtonGroup
                  tipo={tipo}
                  handleChangeTipo={handleChangeTipo}
                  fromTab={fromTab}
                />
              </Box>

              {SelectedDownloadComponent && (
                <Box sx={{ pl: { xs: 0, sm: 1.5 } }}>
                  <SelectedDownloadComponent
                    setDownloadBudgetConfig={setDownloadBudgetConfig}
                    fromTab={fromTab}
                    {...(isCertTab && tipo === DOWNLOAD_TYPES.EXCEL
                      ? {
                          certificationAtOrigin: excelCertificationAtOrigin,
                          setCertificationAtOrigin:
                            setExcelCertificationAtOrigin,
                        }
                      : {})}
                    {...(isCertTab && tipo === DOWNLOAD_TYPES.PDF
                      ? {
                          certificationAtOrigin: pdfCertificationAtOrigin,
                          setCertificationAtOrigin: setPdfCertificationAtOrigin,
                          includeBudget: pdfIncludeBudget,
                          setIncludeBudget: setPdfIncludeBudget,
                          certificationPdfFormat: pdfCertificationFormat,
                          setCertificationPdfFormat: setPdfCertificationFormat,
                          includeUncertifiedLineItems:
                            pdfIncludeUncertifiedLineItems,
                          setIncludeUncertifiedLineItems:
                            setPdfIncludeUncertifiedLineItems,
                        }
                      : {})}
                    {...selectedDownloadData}
                  />
                </Box>
              )}

              <Box sx={{ pl: { xs: 0, sm: 1.5 } }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 300,
                    fontSize: 13,
                    color: MODULE_TITLE_COLOR,
                    mt: 1,
                  }}
                >
                  {i18n.t("downloadBudget.downloadLocation")}
                </Typography>
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              flexShrink: 0,
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 1,
              px: 2,
              py: 2,
              borderTop: "1px solid #eee",
            }}
          >
            {tipo === DOWNLOAD_TYPES.PDF && (
              <Button
                type="button"
                color="inherit"
                variant="outlined"
                onClick={handlePreview}
                disabled={pdfCertificationAtOrigin}
                data-testid="download-budget-preview-button"
              >
                Vista previa
              </Button>
            )}

            <Button
              type="submit"
              color="branded"
              variant="contained"
              autoFocus
              disabled={isDownloadPending}
              aria-busy={isDownloadPending}
              data-amplify-analytics-on="click"
              data-amplify-analytics-name="BudgetDownload"
              data-amplify-analytics-attrs={projectInfo}
              data-element="downloadBudgetBtn"
              data-testid="download-budget-submit-button"
              sx={{
                minWidth: 140,
                ...(isDownloadPending && {
                  "&.Mui-disabled": { opacity: 1 },
                }),
              }}
            >
              {isDownloadPending ? (
                <CircularProgress size={22} color="inherit" aria-hidden />
              ) : (
                i18n.t("Descargar")
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      {openPreview && (
        <ModalPDFPreview
          open={openPreview}
          onClose={() => setOpenPreview(false)}
          onDownload={handleDownload}
          options={getPreviewOptions() || {}}
        />
      )}
      {openBlockModal && (
        <DownloadBlockModal
          open={openBlockModal}
          setOpen={setOpenBlockModal}
          name={name}
          tipo={tipo}
          typeBudget={typeBudget}
          muestraP={true}
          muestraUser={excelData.clientPrice}
          muestrCompany={pdfData.companyAndCustomerInfo}
          muestraDescription={DESCRIPTION}
          soyFinal={soyFinal}
        />
      )}
      {openError && (
        <ModalCallError
          open={openError}
          setOpen={setOpenError}
          error={errorToSend}
          ubicacion={"modal Download Presupuesto"}
        />
      )}
      {openModalWaitProcess &&
        !isNil(processState) &&
        processState.PK &&
        processState.SK && (
          <ModalWaitProcess
            PK={processState.PK}
            SK={processState.SK}
            open={openModalWaitProcess}
            setOpen={setOpenModalWaitProcess}
          />
        )}
      {entitlementDialog}
    </>
  )
}
DownloadBudget.propTypes = {
  open: propTypes.bool.isRequired,
  setOpen: propTypes.func.isRequired,
  name: propTypes.string.isRequired,
  soyFinal: propTypes.bool,
  fromTab: propTypes.oneOf(Object.values(DownloadBudgetEnum)),
}
export default DownloadBudget
